import { memo } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { NavLink, useNavigate } from './nav'
import './TabNavigation.css'

interface Tab {
  id: string
  label: string
  /** Optional leading icon rendered before the label. */
  icon?: ReactNode
  /** Optional count badge (e.g. open bugs). Rendered only when > 0. */
  badge?: number | null
}

interface TabNavigationProps {
  tabs: Tab[]
  activeTab: string
  /** Absolute base path without trailing slash, e.g. `/projects/proj-1` */
  basePath: string
}

export default memo(function TabNavigation({ tabs, activeTab, basePath }: TabNavigationProps) {
  const navigate = useNavigate()
  const root = basePath.replace(/\/$/, '')

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const currentIndex = tabs.findIndex((t) => t.id === activeTab)
    let nextIndex = -1
    if (e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = tabs.length - 1
    }
    const nextTab = nextIndex >= 0 ? tabs[nextIndex] : undefined
    if (nextTab) {
      e.preventDefault()
      navigate(`${root}/${nextTab.id}`)
      queueMicrotask(() => {
        ;(e.currentTarget as HTMLElement)
          .querySelector<HTMLElement>(`[data-testid="tab-${nextTab.id}"]`)
          ?.focus()
      })
    }
  }

  return (
    <div
      className="tab-navigation"
      role="tablist"
      aria-label="Project sections"
      onKeyDown={handleKeyDown}
      data-testid="tab-navigation"
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.id}
          to={`${root}/${tab.id}`}
          role="tab"
          aria-selected={activeTab === tab.id}
          tabIndex={activeTab === tab.id ? 0 : -1}
          id={`tab-${tab.id}`}
          className={({ isActive }) =>
            `tab-navigation__tab ${isActive ? 'tab-navigation__tab--active' : ''}`
          }
          data-testid={`tab-${tab.id}`}
          data-analytics-event={`project-tab-${tab.id}`}
        >
          {tab.icon && <span className="tab-navigation__tab-icon" aria-hidden>{tab.icon}</span>}
          <span className="tab-navigation__tab-label">{tab.label}</span>
          {typeof tab.badge === 'number' && tab.badge > 0 && (
            <span className="tab-navigation__tab-badge" data-testid={`tab-badge-${tab.id}`}>{tab.badge}</span>
          )}
        </NavLink>
      ))}
    </div>
  )
})
